import React, {
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal, Spin } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { DayJs } from '../../common/Utils';
import {
  CashActivity,
  useBusinessContext,
} from '../../context/BusinessContext';
import { useConnection, useUser } from '../../connection/connection';
import { useEvents } from '../../common/EventContext';
import { AlertFilled, DeleteOutlined } from '@ant-design/icons';
import { OptionedInputValue } from '../../common/OptionedInput';
import { useCustomRates } from '../../common/CustomRates';

export type TransactionModalInstance = {
  open: (
    type: 'deposit' | 'withdraw',
    siteId?: string,
    accountId?: string,
  ) => void;
  openTx: (txId: number) => void;
};

type Props = {};

const TransactionModal: ForwardRefRenderFunction<
  TransactionModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const business = useBusinessContext();
  const connection = useConnection();
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const events = useEvents();
  const [title, setTitle] = useState<string>('Create new transaction');

  const [category, setCategory] = useState<string>('transfer');
  const [txType, setTxType] = useState<'deposit' | 'withdraw'>('withdraw');
  const [txId, setTxId] = useState<number>();
  const [selectedCurrencyAmount, setSelectedCurrencyAmount] = useState<
    OptionedInputValue | undefined
  >(undefined);
  const [otherAccountCurrency, setOtherAccountCurrency] = useState<
    string | undefined
  >(undefined);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { clearCustomRates, getCustomRate, setCustomRate, hasRateChanged } =
    useCustomRates();

  useEffect(() => {
    if (
      selectedCurrencyAmount?.option &&
      otherAccountCurrency &&
      selectedCurrencyAmount.option !== otherAccountCurrency
    ) {
      business
        .getCurrencyRate(
          selectedCurrencyAmount.option,
          otherAccountCurrency,
          selectedDate,
        )
        .then((rate) => {
          setCustomRate(
            selectedDate,
            selectedCurrencyAmount.option,
            otherAccountCurrency,
            rate.rate,
            false,
            false,
          );
        });
    }
  }, [
    selectedCurrencyAmount,
    otherAccountCurrency,
    // business.currencyRates,
    business,
    selectedDate,
    setCustomRate,
  ]);

  const user = useUser();
  useImperativeHandle(
    ref,
    () => ({
      open: (type, siteId?: string, accountId?: string) => {
        setVisible(true);
        form.resetFields();
        setTxType(type);
        setTxId(undefined);
        setTitle('Create new transaction');
        setCategory('transfer');
        setSelectedCurrencyAmount(undefined);
        setOtherAccountCurrency(undefined);
        setSelectedDate(new Date());
        clearCustomRates();
        form.setFieldsValue({
          date: DayJs().format('YYYY-MM-DD'),
          siteId: siteId ?? user.siteId,
          siteId2: user.siteId,
          category: 'transfer',
          accountId: accountId,
          type,
        });
      },
      openTx: (txId: number) => {
        setVisible(true);
        setLoading(true);
        setTxId(txId);
        setTitle('Edit transaction');
        form.resetFields();
        connection
          .get(`transactions/${business.businessId}/activities/${txId}`)
          .then((res) => {
            const data: CashActivity = res.data;
            setSelectedDate(new Date(data.date));
            setCategory(res.data.category ?? 'transfer');
            setTxType(res.data.type ?? 'withdraw');
            setSelectedCurrencyAmount({
              option: business.accounts[data.items[0].accountId]?.currency,
              input: Math.abs(data.items[0].amount).toString(),
            });
            setOtherAccountCurrency(
              business.accounts[data.items[1].accountId]?.currency,
            );

            const fcDetails = data.items[1].fcDetails;
            if (fcDetails) {
              setCustomRate(
                new Date(data.date),
                fcDetails.accountCcy,
                fcDetails.receivedCcy,
                fcDetails.exchangeRate,
                true,
                true,
              );
            } else {
              clearCustomRates();
            }

            form.setFieldsValue({
              date: data.date,
              type: data.type,
              notes: data.notes,
              accountId: data.items[0].accountId,
              siteId: data.items[0].siteId,
              description: data.items[0].description,
              category: data.category,
              amount: {
                option: business.accounts[data.items[0].accountId]?.currency,
                input: Math.abs(data.items[0].amount).toString(),
              },
              siteId2: data.items[1].siteId,
              accountId2: data.items[1].accountId,
              currencyRateInfo: fcDetails && {
                sourceCurrency: fcDetails.accountCcy,
                targetCurrency: fcDetails.receivedCcy,
                rate: fcDetails.exchangeRate,
                sourceCurrencyAmount: fcDetails.accountCcyAmount,
              },
            });
          })
          .finally(() => setLoading(false));
      },
    }),
    [
      business.businessId,
      business.accounts,
      connection,
      form,
      user.siteId,
      setCustomRate,
      clearCustomRates,
    ],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'date',
        type: 'date',
        name: 'Date',
        layout: { xs: 12 },
        props: {
          disabledDate: (current: any) => {
            return current && current > DayJs().endOf('day');
          },
        },
        options: {
          rules: [{ required: true, message: 'Date is required' }],
        },
      },
      {
        id: 'type',
        type: 'select2',
        name: 'Type',
        layout: { xs: 12 },
        props: {
          options: [
            { label: 'Deposit', value: 'deposit' },
            { label: 'Withdraw', value: 'withdraw' },
          ],
        },
        options: {
          rules: [{ required: true, message: 'Type is required' }],
        },
      },
      {
        id: 'description',
        type: 'string',
        name: 'Description',
        layout: { xs: 24 },
        props: { placeholder: 'Write a Description' },
        options: {
          rules: [{ required: true, message: 'Description is required' }],
        },
      },
      {
        id: 'siteId',
        type: 'select2',
        name: 'Site',
        layout: { xs: 12 },
        props: {
          showSearch: true,
          optionFilterProp: 'label',
          options: business.select.sites,
        },
        options: {
          rules: [{ required: true, message: 'Site is required' }],
        },
      },
      {
        id: 'accountId',
        type: 'select2',
        name: 'Account',
        layout: { xs: 12 },
        props: (values: any) => {
          return {
            showSearch: true,
            optionFilterProp: 'label',
            options: Object.values(business.accounts)
              .filter((acc) => acc.activity)
              .filter((acc) => !acc.siteId || acc.siteId === values.siteId)
              .map((acc) => ({ label: acc.name, value: acc.accountId })),
          };
        },
        options: {
          rules: [{ required: true, message: 'Account is required' }],
        },
      },
      {
        id: 'amount',
        type: 'optionedInput',
        name: 'Amount',
        layout: { xs: 12 },
        props: (values: any) => {
          return {
            optionsWidth: '70px',
            inputType: 'price',
            readonlyOptions: true,
            values: selectedCurrencyAmount,
            options: Object.keys(business.currencies),
            onChange: (value: OptionedInputValue | undefined) => {
              setSelectedCurrencyAmount(value);
            },
          };
        },
        options: {
          rules: [
            {
              required: true,
              validator: async (_: any, value?: OptionedInputValue) => {
                if (!value?.option || !value?.input) {
                  return Promise.reject('Amount is required');
                }
              },
            },
          ],
        },
      },
      {
        id: 'category',
        type: 'select2',
        name: 'Category',
        layout: { xs: 12 },
        props: {
          showSearch: true,
          optionFilterProp: 'label',
          options:
            txType === 'withdraw'
              ? [
                  { label: 'Transfer to Other Account', value: 'transfer' },
                  { label: 'Withdraw for Expense', value: 'expense' },
                  { label: 'Pay Liability', value: 'liability' },
                  { label: 'Acquire Asset', value: 'asset' },
                  { label: 'Equity', value: 'equity' },
                ]
              : [
                  { label: 'Transfer from Other Account', value: 'transfer' },
                  { label: 'Deposit from Income', value: 'income' },
                  { label: 'Receive Liability', value: 'liability' },
                  { label: 'Sell Asset', value: 'asset' },
                  { label: 'Equity', value: 'equity' },
                ],
        },
        options: {
          rules: [{ required: true, message: 'Category is required' }],
        },
      },
      {
        id: 'currencyRateInfo',
        type: 'currencyRate',
        layout: { xs: 24 },
        visible:
          !!selectedCurrencyAmount?.option &&
          !!otherAccountCurrency &&
          selectedCurrencyAmount?.option !== otherAccountCurrency,
        props: () => {
          return {
            id: 'tx',
            sourceCurrency: selectedCurrencyAmount?.option,
            targetCurrency: otherAccountCurrency,
            currencyRate: getCustomRate(
              selectedDate,
              selectedCurrencyAmount?.option,
              otherAccountCurrency,
            ),
            sourceCurrencyAmount: parseFloat(
              selectedCurrencyAmount?.input ?? '0',
            ),
            resettable: {
              canReset: hasRateChanged(
                selectedDate,
                selectedCurrencyAmount?.option,
                otherAccountCurrency,
              ),
              onReset: async () => {
                if (!selectedCurrencyAmount?.option || !otherAccountCurrency)
                  return;
                return (
                  await business.getCurrencyRate(
                    selectedCurrencyAmount?.option,
                    otherAccountCurrency,
                    selectedDate,
                  )
                ).rate;
              },
            },
          };
        },
      },
      {
        id: 'siteId2',
        type: 'select2',
        name: 'Other Party Site',
        layout: { xs: 12 },
        props: {
          showSearch: true,
          optionFilterProp: 'label',
          options: business.select.sites,
        },
        options: {
          rules: [{ required: true, message: 'Other party site is required' }],
        },
      },
      {
        id: 'accountId2',
        type: 'select2',
        name: 'Other Party Account',
        layout: { xs: 12 },
        props: (values: any) => {
          return {
            showSearch: true,
            optionFilterProp: 'label',
            placeholder: 'Other Party Account',
            options: Object.values(business.accountTypes)
              .filter((at) => {
                if (category === 'transfer') {
                  return at.activity;
                }
                if (category === 'expense') {
                  return at.accountCategory === 'Expenses';
                }
                if (category === 'income') {
                  return at.accountCategory === 'Income';
                }
                if (category === 'asset') {
                  return at.accountCategory === 'Assets' && !at.activity;
                }
                if (category === 'liability') {
                  return at.accountCategory === 'Liabilities' && !at.activity;
                }
                if (category === 'equity') {
                  return at.accountCategory === 'Equity';
                }
                return false;
              })
              .map((at) => ({
                label: at.name,
                options: Object.values(business.accounts)
                  .filter((acc) => acc.accountTypeId === at.accountTypeId)
                  .filter((acc) => !acc.siteId || acc.siteId === values.siteId2)
                  .filter(
                    (acc) =>
                      !(
                        acc.accountId === values.accountId &&
                        values.siteId === values.siteId2 &&
                        (!acc.siteId || acc.siteId === values.siteId)
                      ),
                  )
                  .map((acc) => ({ label: acc.name, value: acc.accountId })),
              }))
              .filter((at) => at.options.length > 0),
          };
        },
        options: {
          rules: [
            { required: true, message: 'Other party account is required' },
          ],
        },
      },
      {
        id: 'notes',
        type: 'text',
        name: 'Notes',
      },
      {
        id: 'receipt',
        type: 'upload',
        name: 'Receipt',
      },
    ];
    return formSchema;
  }, [
    business,
    selectedDate,
    category,
    txType,
    selectedCurrencyAmount,
    otherAccountCurrency,
    getCustomRate,
    hasRateChanged,
  ]);

  const submit = useCallback(
    (values: any) => {
      setSaving(true);

      const amountFloat = parseFloat(values.amount.input);
      const sourceCurrency = values.currencyRateInfo?.sourceCurrency;
      const targetCurrency = values.currencyRateInfo?.targetCurrency;
      const rate = values.currencyRateInfo?.rate ?? 0;
      const amount = values.type === 'withdraw' ? -amountFloat : amountFloat;

      const data = {
        businessId: business.businessId,
        date: values.date,
        type: values.type,
        category: values.category,
        notes: values.notes,
        userId: user.userId,
        items: [
          {
            accountId: values.accountId,
            siteId: values.siteId,
            description: values.description,
            amount: amount,
            txType: 'transfer',
            otherSide: `${values.accountId2}-${values.siteId2}`,
            currency: sourceCurrency,
          },
          {
            accountId: values.accountId2,
            siteId: values.siteId2,
            description: values.description,
            amount: -amount,
            txType: 'transfer',
            otherSide: `${values.accountId}-${values.siteId}`,
            fcDetails:
              sourceCurrency === targetCurrency ||
              !targetCurrency ||
              !sourceCurrency
                ? undefined
                : {
                    accountCcy: sourceCurrency,
                    receivedCcy: targetCurrency,
                    accountCcyAmount: rate,
                    receivedCcyAmount: Math.abs(amount) * rate,
                    exchangeRate: rate,
                  },
          },
        ],
      };

      (txId
        ? connection.patch(
            `transactions/${business.businessId}/activities/${txId}`,
            data,
          )
        : connection.post(
            `transactions/${business.businessId}/activities`,
            data,
          )
      )
        .then((res) => {
          setVisible(false);
          events.emit('TRANSACTIONS', {
            action: 'CREATE',
            data: res.data.transactions,
          });
          events.emit('ACTIVITIES', {
            action: 'CREATE',
            data: res.data.activities,
          });
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [business.businessId, connection, events, txId, user.userId],
  );

  const deleteTx = useCallback(() => {
    setSaving(true);
    connection
      .delete(`transactions/${business.businessId}/activities/${txId}`)
      .then((res) => {
        setVisible(false);
        events.emit('TRANSACTIONS', {
          action: 'DELETE',
          data: txId,
        });
        events.emit('ACTIVITIES', {
          action: 'DELETE',
          data: txId,
        });
      })
      .finally(() => {
        setSaving(false);
      });
  }, [business.businessId, connection, events, txId]);

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          {txId && (
            <Button
              onClick={() =>
                Modal.confirm({
                  title: 'Delete Transaction',
                  content: 'Are you sure you want to delete this transaction?',
                  okButtonProps: { danger: true },
                  onOk: deleteTx,
                })
              }
              danger={true}
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          )}
          <div className="w-full flex justify-end gap-1">
            <Button onClick={() => setVisible(false)}>Cancel</Button>
            <Button
              onClick={() => form.submit()}
              type="primary"
              loading={saving}
            >
              Save
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Spin spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onFinish={submit}
          onValuesChange={(changedValues, values) => {
            if (changedValues.category) {
              setCategory(changedValues.category);
              form.setFieldsValue({ accountId2: undefined });
            }

            if (changedValues.type) {
              setTxType(changedValues.type);
              setCategory('transfer');
              form.setFieldsValue({
                category: 'transfer',
                accountId2: undefined,
              });
            }

            if (changedValues.accountId) {
              setSelectedCurrencyAmount({
                option: business.accounts[values.accountId]?.currency,
                input: selectedCurrencyAmount?.input,
              });
            }

            if (changedValues.accountId2) {
              setOtherAccountCurrency(
                business.accounts[values.accountId2]?.currency,
              );
            }

            if (changedValues.date) {
              // business.fetchCurrencyRatesForDate(new Date(changedValues.date));
              setSelectedDate(new Date(changedValues.date));
            }

            const rateInfo = changedValues.currencyRateInfo;
            if (
              rateInfo &&
              rateInfo.rate &&
              rateInfo.sourceCurrency &&
              rateInfo.targetCurrency
            ) {
              setCustomRate(
                selectedDate,
                rateInfo.sourceCurrency,
                rateInfo.targetCurrency,
                rateInfo.rate,
                true,
                false,
              );
            }
          }}
        >
          <Form.Items schema={schema} />
        </Form>
      </Spin>
    </Drawer>
  );
};

export const useTransactionModal = () => useRef<TransactionModalInstance>(null);

export default React.forwardRef<TransactionModalInstance, Props>(
  TransactionModal,
);
